import {
  Collapse,
  Divider,
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { ReactComponent as SortableColumn } from "../../../shared/assets/sort_icons/sortable-column.svg";
import { ReactComponent as SortIcon } from "../../../shared/assets/sort_icons/sort-icon.svg";
import { ReactComponent as DownloadIcon } from "../../../shared/assets/general/download.svg";
import { formatAmount } from "../../../shared/utils/utils";
import store, { RootState } from "../../../store/store";
import {
  DASHBOARD_CONSTANTS,
  DATE_FILTERS,
  DATE_FORMAT,
  DEVICE_TYPES,
  PAGES_URL,
  STATUS,
  UPLOAD_FILE_PURPOSE_TYPE,
} from "../../../shared/constants/constants";
import {
  setDateSpanRedux,
  setFilters,
  setPagination,
  setSelectedSort,
  setSelectedTab,
  setStoreSearchText,
} from "../../../store/reducers/dashboard";
import CustomSpinner from "../../../shared/components/customSpinner/customSpinner";
import { getOrderColor } from "../../../shared/utils/colorUtils";
import CustomBadge from "../../../shared/components/customBadge/customBadge";
import { Search, UploadFile } from "@mui/icons-material";
import { uploadFileWrapper } from "../../../shared/utils/fileUtils";
import { useSnackbar } from "notistack";
import NavigateBackButton from "../../../shared/components/customNavigateButton/customNavigateButton";
import {
  getAllInvoice,
  getInvoicePdf,
} from "../../../store/api/invoice";
import { updateOrderStatus } from "../../../store/api/purchaseOrder";

const InvoiceTable = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const storeInvoiceState = useSelector((state: RootState) => state.invoice);
  const storePartyState = useSelector((state: RootState) => state.party);
  const storeStatusState = useSelector(
    (state: RootState) => state.dashboard.filters,
  );
  const storePageState = useSelector(
    (state: RootState) => state.dashboard.currentPage,
  );
  const storeRowsPerPageState = useSelector(
    (state: RootState) => state.dashboard.pageSize,
  );
  const storeSortState = {
    type: useSelector((state: RootState) => state.dashboard.sortField),
    value: useSelector((state: RootState) => state.dashboard.sortOrder),
  };
  const storeTabState = useSelector(
    (state: RootState) => state.dashboard.selectedTab,
  );
  const storeSearchTextState = useSelector(
    (state: RootState) => state.dashboard.searchText,
  );
  const storeDateState = useSelector(
    (state: RootState) => state.dashboard.dateSpan,
  );
  const theme = useTheme();
  const isLessThanMd = useMediaQuery(theme.breakpoints.down("md"));
  const isLessThanSm = useMediaQuery(theme.breakpoints.down("sm"));

  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState<any>({});
  const [pageCount, setPageCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState(""); // Debounce state
  const tab = DASHBOARD_CONSTANTS.TABS.INVOICES;
  const [totalRows, setTotalRows] = useState(0);
  const statusList = "";
  const [dateSpan, setDateSpan] = useState<any>({
    // Matches the current-month default now used on the GRN page - no date
    // filter control is rendered on this page either.
    type: DATE_FILTERS.MONTH.value,
    startDate: dayjs(new Date()),
    endDate: dayjs(new Date()),
  });
  const [sort, setSort] = useState<{
    type: string;
    value: "asc" | "desc";
  }>({
    type: "invoiceDate",
    value: "desc",
  });
  const [mount, setMount] = useState(false);
  const [isLoading, setIsLoading] = useState<string>("");
  const [openInvoice, setOpenInvoice] = useState<string>("");

  // Debounce side-effect hook
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500); // 500ms typing delay threshold

    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  useEffect(() => {
    if (mount) {
      //updating redux states for preserving page and sorts
      dispatch(setPagination({ currentPage: page, pageSize: rowsPerPage }));
      dispatch(setFilters(status));
      dispatch(setSelectedTab(tab));
      dispatch(
        setSelectedSort({
          sortField: sort.type,
          sortOrder: sort.value === "asc" ? "asc" : "desc",
        }),
      );
      dispatch(setStoreSearchText(debouncedSearchText));
      dispatch(
        setDateSpanRedux({
          type: dateSpan.type,
          startDate: dateSpan.startDate.toString(),
          endDate: dateSpan.endDate.toString(),
        }),
      );
      fetchRequests(debouncedSearchText);
    }
  }, [
    page,
    rowsPerPage,
    status,
    tab,
    sort,
    statusList,
    debouncedSearchText,
    dateSpan,
    storePartyState.currentOrg,
    mount,
  ]);

  useEffect(() => {
    // Set the initial local state from Redux values on mount
    if (storeTabState === DASHBOARD_CONSTANTS.TABS.INVOICES) {
      setStatus(storeStatusState || "");
      setPage(storePageState || 1);
      setRowsPerPage(storeRowsPerPageState || 10);
      setSort({
        type: storeSortState.type || "invoiceDate",
        value: storeSortState.value || "desc",
      });
      setSearchText(storeSearchTextState || "");
      setDebouncedSearchText(storeSearchTextState || "");
      setDateSpan({
        type: storeDateState.type || DATE_FILTERS.MONTH.value,
        startDate: dayjs(storeDateState.startDate) || dayjs(new Date()),
        endDate: dayjs(storeDateState.endDate) || dayjs(new Date()),
      });
    }
    setMount(true);
  }, []);

  const handleSearch = (searchString: string) => {
    setSearchText(searchString);
    setPage(1); // Reset page to 1 when a new search is initiated
  };

  // The list only shows invoices that still need a signed invoice uploaded,
  // which isn't a column we can filter on in the DB (upload status lives in
  // content-service, keyed by order). So we still fetch a bounded buffer and
  // filter/paginate client-side - but the upload-status check itself is now
  // done once, server-side, for the whole buffer (via uploadStatusPurposeType)
  // instead of one request per invoice.
  const INVOICE_BUFFER_SIZE = 200;
  // Only invoices from 1-Aug-2026 IST onward are shown. Sent as a plain
  // YYYY-MM-DD string; the backend anchors it to IST midnight via
  // getISTStartOfDay, so this is timezone-safe regardless of the client's
  // local timezone. Keep this in sync with the same cutoff used in
  // grnTable.tsx for the delivery challans list.
  const AUG_2026_CUTOFF = "2026-08-01";

  const fetchRequests = (searchString: any) => {
    let inputSearchText = debouncedSearchText;
    if (searchString && searchString != "") {
      inputSearchText = searchString;
    }
    const queryParams = {
      page: 1,
      sort: sort.type,
      sortOrder: sort.value,
      pageSize: INVOICE_BUFFER_SIZE,
      startDate: AUG_2026_CUTOFF,
      endDate: "",
      searchText: inputSearchText,
      uploadStatusPurposeType: UPLOAD_FILE_PURPOSE_TYPE.SIGNED_INVOICE,
    };
    store
      .dispatch(
        getAllInvoice({
          queryParams: queryParams,
          body: {},
        }),
      )
      .unwrap()
      .then((res: any) => {
        const needsSignedInvoice = (res.data?.invoices ?? []).filter(
          (invoice: any) => !invoice.contentUploaded,
        );
        const totalRecords = needsSignedInvoice.length;
        const startIndex = (page - 1) * rowsPerPage;
        setData(needsSignedInvoice.slice(startIndex, startIndex + rowsPerPage));
        setTotalRows(totalRecords);
        const cnt = Math.ceil(totalRecords / rowsPerPage);
        setPageCount(cnt);
      })
      .catch((err) => {
        enqueueSnackbar(`Error fetching invoice list: ${err?.toString()}`, {
          variant: "error",
        });
      });
  };

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleSort = (column: string) => {
    setPage(1);

    if (sort.type === column) {
      setSort({
        type: column,
        value: sort.value === "asc" ? "desc" : "asc",
      });
    } else {
      setSort({
        type: column,
        value: "asc",
      });
    }
  };

  const handleDownloadInvoicePdf = async (invoice: any) => {
    const reqBody = {
      invoiceId: invoice?.invoiceId,
    };
    store
      .dispatch(getInvoicePdf(reqBody))
      .unwrap()
      .catch((error) => {
        enqueueSnackbar(
          error?.error ??
            error?.data?.message ??
            "Error downloading invoice pdf",
          { variant: "error" },
        );
      });
  };

  const handleChangeDateType = (event: SelectChangeEvent) => {
    setPage(1);
    setDateSpan({
      ...dateSpan,
      type: event.target.value,
    });
  };

  const uploadDocument = async (file: any, type: string, invoice: any) => {
    try {
      setIsLoading(invoice?.invoiceId + "-" + "DocUpload");
      enqueueSnackbar("Uploading Invoice", { variant: "info" });

      await uploadFileWrapper(file, type, invoice?.order?._id);
      enqueueSnackbar(
        `${file[0].name} Uploaded Successfully for Invoice: ${invoice?.invoiceCode}`,
        { variant: "success" },
      );

      enqueueSnackbar(
        `Accepting three way match for ${invoice?.order?.orderCode}`,
        { variant: "info" },
      );

      let reqBody: any = {
        orderId: invoice?.order?._id,
        comments: "Approved due to signed invoice",
        invoiceIdList: [invoice?.invoiceId],
        threeWayMatchResult: STATUS.THREE_WAY_MATCH_STATUS.APPROVED,
      };

      await store.dispatch(updateOrderStatus(reqBody)).unwrap();

      enqueueSnackbar("Updated Status Successfully", { variant: "success" });
      fetchRequests(debouncedSearchText);
    } catch (error) {
      if (error instanceof AggregateError) {
        // Handle multiple errors
        console.error(`Aggregate Error caught: ${error.message}`);

        error.errors.forEach((subError) => {
          enqueueSnackbar(`${subError.message}`, { variant: "error" });
        });
      } else if (error instanceof Error) {
        // Handle standard errors (TypeError, ReferenceError, custom errors, etc.)
        console.log(error);
        enqueueSnackbar(`${error.message}`, { variant: "error" });
      }
    } finally {
      setIsLoading("");
    }
  };

  const handleStatus = async (orderDetails: any, invoiceId: string) => {
    let reqBody: any = {
      orderId: orderDetails?._id,
      status: orderDetails?.statusId,
      comments: "Approved due to signed invoice",
      invoiceIdList: [invoiceId],
      threeWayMatchResult: STATUS.THREE_WAY_MATCH_STATUS.APPROVED,
    };
    await store
      .dispatch(updateOrderStatus(reqBody))
      .unwrap()
      .then((res) => {
        enqueueSnackbar("Updated Status Successfully", { variant: "success" });
      })
      .catch(() => {
        enqueueSnackbar("Update Status Failed", { variant: "error" });
      });
  };

  interface Column {
    id: string;
    label: string;
    width?: string;
    align?: "right" | "center" | "left";
    enableSort?: boolean;
    format?: (value: number) => string;
  }

  const invoiceItemColumns: Column[] = [
    {
      id: "_id",
      label: "Order ID",
      width: "12%",
      enableSort: true,
      align: "center",
    },
    {
      id: "invoiceDate",
      label: "Invoice Date",
      width: "15%",
      enableSort: true,
      align: "center",
    },
    {
      id: "invoiceCode",
      label: "Invoice Code",
      width: "18%",
      align: "center",
    },
    {
      id: "amount",
      label: "Amount",
      width: "21%",
      align: "center",
    },
    {
      id: "partyIdTo",
      label: "Billed To",
      width: "15%",
      enableSort: true,
      align: "center",
    },
    {
      id: "actionsId",
      label: "Actions",
      width: "19%",
      enableSort: false,
      align: "center",
    },
  ];

  const invoiceTableRows = useMemo(() => {
    switch (storeInvoiceState.loading) {
      case true:
      case undefined:
        return (
          <TableRow sx={{ height: "80%" }}>
            <TableCell
              align="center"
              sx={{ borderBottom: "none" }}
              colSpan={invoiceItemColumns.length}
            >
              <CustomSpinner size={"2rem"} />
            </TableCell>
          </TableRow>
        );
      case false:
        return data?.length ? (
          data.map((invoice: any) => {
            if (isLessThanMd) {
              return (
                <Stack width={"100%"}>
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    onClick={() => {
                      openInvoice === invoice?.invoiceid
                        ? setOpenInvoice("")
                        : setOpenInvoice(invoice?.invoiceId);
                    }}
                  >
                    <Typography>
                      {invoice?.invoiceCode?.split("/")?.splice(-1)}
                    </Typography>
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"center"}
                    >
                      {isLoading === invoice.invoiceId + "-" + "DocUpload" ? (
                        <CustomSpinner />
                      ) : (
                        <IconButton
                          sx={{
                            borderRadius: 0,
                            mr: 1,
                          }}
                          size="large"
                        >
                          <UploadFile height={"20px"} width={"20px"} />
                          <Input
                            type="file"
                            id="signedFileUpload"
                            onChange={(event) => {
                              if (event?.target instanceof HTMLInputElement) {
                                uploadDocument(
                                  event?.target?.files,
                                  "signedInvoice",
                                  invoice,
                                );
                              }
                            }}
                            sx={{
                              width: 1,
                              height: 1,
                              opacity: 0,
                              border: "none",
                              position: "absolute",
                              top: "0",
                              left: "0",
                              right: "0",
                              bottom: "0",
                              cursor: "pointer",
                            }}
                          />
                        </IconButton>
                      )}
                      <IconButton
                        sx={{
                          borderRadius: 0,
                          mr: 1,
                        }}
                        onClick={() => handleDownloadInvoicePdf(invoice)}
                        size="large"
                      >
                        <DownloadIcon height={"20px"} width={"20px"} />
                      </IconButton>
                    </Stack>
                  </Stack>
                  <Collapse in={openInvoice === invoice.invoiceId}>
                    <Stack direction={"row"} spacing={1}>
                      <Typography>Order: </Typography>
                      <Typography>
                        {invoice?.order?.referenceNumber &&
                        invoice?.order?.referenceNumber !== ""
                          ? invoice?.order?.referenceNumber
                          : ((invoice?.order?.orderCode
                              ? invoice?.order?.orderCode.split("/")[5]
                              : invoice?.order?._id) ?? "-")}
                      </Typography>
                    </Stack>
                    <Stack direction={"row"} spacing={1}>
                      <Typography>Billed To: </Typography>
                      <Typography>{invoice?.partyIdToName}</Typography>
                    </Stack>
                    <Stack direction={"row"} spacing={1}>
                      <Typography>Amount: </Typography>
                      <Typography>{formatAmount(invoice?.amount)}</Typography>
                    </Stack>
                    <Stack direction={"row"} spacing={1}>
                      <Typography>Invoice Date</Typography>
                      <Typography>
                        {dayjs(invoice?.invoiceDate).format(
                          DATE_FORMAT.ISO_FORMAT,
                        )}
                      </Typography>
                    </Stack>
                  </Collapse>
                </Stack>
              );
            } else {
              return (
                <TableRow key={invoice.invoiceId}>
                  <TableCell align={invoiceItemColumns[0].align}>
                    <Typography sx={{ wordBreak: "break-all" }}>
                      {invoice?.order?.referenceNumber &&
                      invoice?.order?.referenceNumber !== ""
                        ? invoice?.order?.referenceNumber
                        : ((invoice?.order?.orderCode
                            ? invoice?.order?.orderCode.split("/")[5]
                            : invoice?.order?._id) ?? "-")}
                    </Typography>
                  </TableCell>
                  <TableCell align={invoiceItemColumns[1].align}>
                    <Typography sx={{ wordBreak: "break-all" }}>
                      {dayjs(invoice?.invoiceDate).format(
                        DATE_FORMAT.ISO_FORMAT,
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell align={invoiceItemColumns[2].align}>
                    <Typography sx={{ wordBreak: "break-all" }}>
                      {invoice?.invoiceCode?.split("/")?.splice(-1)}
                    </Typography>
                  </TableCell>

                  <TableCell align={invoiceItemColumns[3].align}>
                    <Typography sx={{ wordBreak: "break-all" }}>
                      {formatAmount(invoice?.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell align={invoiceItemColumns[4].align}>
                    <Typography sx={{ wordBreak: "break-all" }}>
                      {invoice?.partyIdToName}
                    </Typography>
                  </TableCell>
                  <TableCell align={invoiceItemColumns[5].align}>
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"center"}
                    >
                      {invoice?.order &&
                        Object.keys(invoice?.order).length > 0 && (
                          <Stack>
                            {isLoading ===
                            invoice.invoiceCode + "-" + "DocUpload" ? (
                              <CustomSpinner />
                            ) : (
                              <IconButton
                                sx={{
                                  borderRadius: 0,
                                  mr: 1,
                                }}
                                size="large"
                              >
                                <UploadFile height={"20px"} width={"20px"} />
                                <Input
                                  type="file"
                                  id="signedFileUpload"
                                  onChange={(event) => {
                                    if (
                                      event?.target instanceof HTMLInputElement
                                    ) {
                                      uploadDocument(
                                        event?.target?.files,
                                        "signedInvoice",
                                        invoice,
                                      );
                                    }
                                  }}
                                  sx={{
                                    width: 1,
                                    height: 1,
                                    opacity: 0,
                                    border: "none",
                                    position: "absolute",
                                    top: "0",
                                    left: "0",
                                    right: "0",
                                    bottom: "0",
                                    cursor: "pointer",
                                  }}
                                />
                              </IconButton>
                            )}
                          </Stack>
                        )}
                      <IconButton
                        sx={{
                          borderRadius: 0,
                          mr: 1,
                        }}
                        onClick={() => handleDownloadInvoicePdf(invoice)}
                        size="large"
                      >
                        <DownloadIcon height={"20px"} width={"20px"} />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            }
          })
        ) : (
          <TableRow>
            <TableCell
              sx={{ borderBottom: "none" }}
              colSpan={invoiceItemColumns.length}
            >
              No Data
            </TableCell>
          </TableRow>
        );
    }
  }, [
    invoiceItemColumns.length,
    data,
    storeInvoiceState.loading,
    isLoading,
    isLessThanMd,
    openInvoice,
  ]);

  return (
    <Stack width={"100%"} p={2} boxSizing="border-box">
      <NavigateBackButton
        label={"Back to Home"}
        navigateLink={PAGES_URL.HOME}
      />

      <Stack
        direction={{
          xs: "column",
          sm: "row",
        }}
        justifyContent="space-between"
        alignItems={{
          sm: "flex-start",
          xs: "center",
        }}
        spacing={2}
        my={1}
      >
        <Typography fontSize={26} fontWeight={600}>
          Invoice List
        </Typography>
        <FormControl sx={{ m: 1 }} variant="standard" fullWidth={isLessThanSm}>
          <InputLabel htmlFor={`search-input`}>Search</InputLabel>
          <Input
            id={`search-input`}
            value={searchText} // Controlled input to ensure text syncs on initial mount
            startAdornment={
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            }
            onChange={(e) => handleSearch(e.target.value)}
          />
        </FormControl>
      </Stack>
      {!isLessThanMd && (
        <TableContainer>
          <Table stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                {invoiceItemColumns.map((invoiceItemColumn) => (
                  <TableCell
                    align={invoiceItemColumn.align}
                    key={invoiceItemColumn.id}
                    sx={{
                      width: invoiceItemColumn.width,
                    }}
                  >
                    {invoiceItemColumn.enableSort && (
                      <TableSortLabel
                        active={true}
                        direction={sort.value}
                        hideSortIcon={true}
                        onClick={() => handleSort(invoiceItemColumn.id)}
                        IconComponent={() => {
                          if (sort.type !== invoiceItemColumn.id) {
                            return <SortableColumn style={{ marginLeft: 2 }} />;
                          } else {
                            return (
                              <SortIcon
                                style={{
                                  transform:
                                    sort.value === "desc"
                                      ? "rotate(180deg)"
                                      : "rotate(0deg)",
                                  marginLeft: 2,
                                }}
                              />
                            );
                          }
                        }}
                      >
                        <p>{invoiceItemColumn.label}</p>
                      </TableSortLabel>
                    )}

                    {!invoiceItemColumn.enableSort && invoiceItemColumn.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>{invoiceTableRows}</TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={invoiceItemColumns.length}>
                  <Stack
                    direction={"row"}
                    display={"flex"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                  >
                    <Typography>
                      Showing data{" "}
                      {totalRows ? (page - 1) * rowsPerPage + 1 : totalRows} to{" "}
                      {(page - 1) * rowsPerPage + rowsPerPage < totalRows
                        ? (page - 1) * rowsPerPage + rowsPerPage
                        : totalRows}{" "}
                      of {totalRows} entries
                    </Typography>
                    <Stack
                      direction={"row"}
                      spacing={1}
                      display={"flex"}
                      alignItems={"center"}
                      justifyContent={"center"}
                    >
                      <Pagination
                        count={pageCount}
                        page={page}
                        onChange={handleChangePage}
                        dir={""}
                      />
                      <Typography>Result Per Page</Typography>
                      <Select
                        labelId="row_count"
                        size="small"
                        id="RowCountField"
                        sx={{
                          ".MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          paddingRight: "0px",
                        }}
                        value={rowsPerPage}
                        onChange={(e) => setRowsPerPage(Number(e.target.value))}
                      >
                        {DASHBOARD_CONSTANTS.PAGE_SIZES.map((key) => (
                          <MenuItem key={key} value={key}>
                            {key}
                          </MenuItem>
                        ))}
                      </Select>
                    </Stack>
                  </Stack>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
      {isLessThanMd && (
        <Stack spacing={1} divider={<Divider variant="fullWidth" />}>
          {invoiceTableRows}
          <Pagination
            count={pageCount}
            page={page}
            onChange={handleChangePage}
            dir={""}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default InvoiceTable;
